import React, { useState, useEffect } from "react";
import { FaUser, FaLock, FaEye, FaEyeSlash, FaArrowLeft } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { auth, googleProvider, db } from "../firebase";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  sendPasswordResetEmail
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { track } from "@vercel/analytics";
import { useAuth } from "../context/AuthContext";
import { ensureCompleteUserDocument } from '../utils/userHelpers';
import styles from "./Login.module.css"; // Using CSS Modules for cleanliness

import SocialSidebar from "../components/SocialSideBar";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (currentUser) {
      navigate("/");
    }
  }, [currentUser, navigate]);

  const ensureUserDoc = async (user) => {
    // Use centralized helper to ensure ALL required fields exist
    await ensureCompleteUserDocument(user);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    track("login_email_submit", { page: window.location.pathname, email });
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (!user.emailVerified) {
        setMessage("Please verify your email before logging in.");
        return;
      }

      await ensureUserDoc(user);
      navigate("/profile");
    } catch (error) {
      setMessage(error.message);
    }
  };

  const handleGoogleLogin = async () => {
    track("login_google_click", { page: window.location.pathname });
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await ensureUserDoc(result.user);
      navigate("/");
    } catch (error) {
      setMessage(error.message);
    }
  };

  const handlePasswordReset = async () => {
    track("login_password_reset", { page: window.location.pathname, email });
    if (!email) {
      setMessage("Please enter your email first.");
      return;
    }
    sendPasswordResetEmail(auth, email)
      .then(() => setMessage("Password reset email sent! Check your inbox."))
      .catch((error) => setMessage(error.message));
  };

  const toggleForgotPassword = () => {
    track("login_toggle_forgot_password", { page: window.location.pathname, isForgotPassword: !isForgotPassword });
    setIsForgotPassword(!isForgotPassword);
    setMessage(""); // Clear messages on toggle
  };

  return (
    <div className={styles.loginContainer}>
      <SocialSidebar />
      
      <div className={styles.loginWrapper}>
        <div className={styles.loginBox}>
          <div className={styles.headerArea}>
            <h1 className={styles.brandName}>PrrepX Pathshala</h1>
            <h2 className={styles.tagline}>
              {isForgotPassword ? "Reset Your Password" : "Unlock Your Potential"}
            </h2>
            <p className={styles.subTagline}>Join the revolution of IIT/NIT guided learning.</p>
          </div>

          <button onClick={handleGoogleLogin} className={styles.googleBtn}>
            <FcGoogle className={styles.googleIcon} /> 
            <span>Continue with Google</span>
          </button>

          <div className={styles.divider}>
            <span>or sign in with email</span>
          </div>

          {isForgotPassword ? (
            <div className={styles.formFadeIn}>
              <div className={styles.inputGroup}>
                <FaUser className={styles.icon} />
                <input
                  type="email"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <button onClick={handlePasswordReset} className={styles.loginBtn}>
                Send Reset Link
              </button>
              <p className={styles.backLink} onClick={toggleForgotPassword}>
                <FaArrowLeft size={12} /> Back to Login
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className={styles.formFadeIn}>
              <div className={styles.inputGroup}>
                <FaUser className={styles.icon} />
                <input
                  type="email"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              
              <div className={`${styles.inputGroup} ${styles.passwordGroup}`}>
                <FaLock className={styles.icon} />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <span className={styles.eyeIcon} onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>

              <div className={styles.formUtils}>
                 <span className={styles.forgotPassword} onClick={toggleForgotPassword}>
                    Forgot Password?
                 </span>
              </div>

              <button type="submit" className={styles.loginBtn}>
                Login to Account
              </button>
            </form>
          )}

          {message && (
            <div className={`${styles.message} ${message.includes('sent') ? styles.success : styles.error}`}>
              {message}
            </div>
          )}

          <p className={styles.signupText}>
            Don’t have an account? <a href="/signup">Create account</a>
          </p>
        </div>
        
        <div className={styles.loginFooter}>
          © 2026 PrrepX Pathshala. Led by IIT/NIT Alumni.
        </div>
      </div>
    </div>
  );
};

export default Login;